import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { FUTURE_FLAGS } from './utils/routerConfig';

// Initialize Sentry as early as possible
import { initializeSentry, Sentry } from './config/sentry.config';

// Initialize Sentry before rendering
initializeSentry();

// Create Sentry-wrapped root
const container = document.getElementById('root');
const root = ReactDOM.createRoot(container);

// Render app with Sentry Error Boundary
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, componentStack, resetError }) => (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-gray-800 rounded-lg shadow-2xl p-8 border border-red-500">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-900 rounded-full mb-4">
                <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Application Error
              </h1>
              <p className="text-gray-300 text-lg">
                Something went wrong. Our team has been notified.
              </p>
            </div>

            <div className="bg-red-900 bg-opacity-30 border border-red-500 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-red-300 mb-2">
                Error Details:
              </h3>
              <p className="text-red-200 text-sm font-mono break-all">
                {error?.toString()}
              </p>
            </div>

            <div className="flex gap-4 justify-center mb-4">
              <button
                onClick={resetError}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                Go Home
              </button>
            </div>

            <div className="text-center mb-4">
              <button
                onClick={() => {
                  try {
                    const { showUserFeedback } = require('./config/sentry.config');
                    showUserFeedback();
                  } catch (e) {
                    console.error('Failed to show feedback:', e);
                  }
                }}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-all duration-200 shadow-lg inline-flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Send Feedback
              </button>
            </div>

            <div className="mt-4 text-center">
              <p className="text-gray-400 text-sm">
                Error has been automatically reported to our monitoring system.
              </p>
            </div>
          </div>
        </div>
      )}
      showDialog
      dialogOptions={{
        title: 'We encountered an error',
        subtitle: 'Our team has been notified and is working on a fix.',
        subtitle2: 'If the problem persists, please contact support.',
      }}
    >
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
